import { ActionIcon, Loader, ThemeIcon } from "@mantine/core";
import IconCardPlus from "@src/assets/icons/lucide/card-plus.svg?react";
import IconAdjustmentsHorizontal from "@src/assets/icons/tabler/adjustments-horizontal.svg?react";
import IconCaretDownFilled from "@src/assets/icons/tabler/caret-down.svg?react";
import IconCaretRightFilled from "@src/assets/icons/tabler/caret-right.svg?react";
import IconFolderPlus from "@src/assets/icons/tabler/folder-plus.svg?react";
import { shareKeySignal } from "@src/auth/auth-utils";
import { useCreateChat } from "@src/components/agent-chat/mutations/chat-mutations";
import { useCreateCardInWorkspace } from "@src/components/board/mutations/create-card-in-workspace";
import { useCreateWhiteboard } from "@src/components/board/mutations/create-whiteboard";
import { useMoveRanklistItem } from "@src/components/board/mutations/move-item";
import { fetchActiveWhiteboardsQueryOptions } from "@src/components/board/queries/fetch-active-whiteboards";
import {
	type NavbarBranchItem,
	fetchNavbarBranchQueryOptions,
	findBranchKeysContainingInSection,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { useWhiteboardNavigation } from "@src/components/board/use-whiteboard-navigation";
import {
	generateWhiteboardName,
	getNextNamedItemIndex,
} from "@src/components/board/utils";
import {
	WhiteboardDocumentId,
	parseChatDocumentId,
	parseWhiteboardDocumentId,
} from "@src/components/collaboration/document-id";
import { useLibraryEntities } from "@src/components/library/queries/library-entities";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import { useAuthenticatedUser } from "@src/components/user/user-context";
import { NavbarSection } from "@src/gql/graphql";
import { Tree, type TreeHandle } from "@src/lib/clean-tree";
import { captureBoardCreationSucceeded } from "@src/utils/analytics";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNavActiveSource } from "../context/nav-active-source-context";
import { useNavPendingRename } from "../context/nav-pending-rename-context";
import { useSearch } from "../context/search-context";
import { NavSectionLoadingItem } from "../nav-section-panel/nav-section-loading-item";
import NavSectionTextLabel from "../nav-section-panel/nav-section-text-label";
import { ManageBoardsModal } from "./manage-boards-modal";
import { MoveToFolderModal } from "./move-to-folder-modal";
import { createNavTreeItemRenderer } from "./nav-tree-item";
import {
	NAV_TREE_OPEN_FOLDER_EVENT,
	NAV_TREE_OPEN_STATE_KEY,
	type NavTreeOpenFolderDetail,
	TREE_INDENT_PER_LEVEL_PX,
	readTreeOpenState,
} from "./tree-indent";
import { useNavBranchItems, useRanklistTree } from "./use-ranklist-tree";

const OPEN_STATE_KEY = NAV_TREE_OPEN_STATE_KEY;

export function NavTreePanel({
	hideSectionHeader = false,
}: {
	hideSectionHeader?: boolean;
} = {}) {
	const queryClient = useQueryClient();
	const authenticatedUser = useAuthenticatedUser();
	const navigate = useNavigate();
	const { setActiveSource } = useNavActiveSource();
	const { navigateToWhiteboard } = useWhiteboardNavigation();
	const { mutateAsync: createWhiteboard, isPending: isCreatingWhiteboard } =
		useCreateWhiteboard();
	const { mutateAsync: moveRanklistItem } = useMoveRanklistItem();
	const { mutateAsync: createChat, isPending: isCreatingChat } =
		useCreateChat();
	const {
		handleMoveItem,
		handleCreateFolder,
		handleDeleteFolder,
		isCreatingFolder,
	} = useRanklistTree();
	const { pendingRenameItemId, requestRename, consumeRename } =
		useNavPendingRename();
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [initialOpenState] = useState(() => readTreeOpenState(OPEN_STATE_KEY));
	const treeRef = useRef<TreeHandle>(null);

	useEffect(() => {
		const handleOpenFolder = (event: Event) => {
			const detail = (event as CustomEvent<NavTreeOpenFolderDetail>).detail;
			if (!detail?.folderIds?.length) return;
			setIsCollapsed(false);
			for (const folderId of detail.folderIds) {
				initialOpenState[folderId] = detail.isOpen;
				treeRef.current?.setItemOpen(folderId, detail.isOpen);
			}
		};
		window.addEventListener(NAV_TREE_OPEN_FOLDER_EVENT, handleOpenFolder);
		return () => {
			window.removeEventListener(NAV_TREE_OPEN_FOLDER_EVENT, handleOpenFolder);
		};
	}, [initialOpenState]);

	const { items: rootItems, isLoading } = useNavBranchItems(null);
	const { query: navSearchQuery } = useSearch();
	const treeDragAndDropEnabled = !navSearchQuery.trim();

	const { data: favouriteBranch } = useQuery(
		fetchNavbarBranchQueryOptions(NavbarSection.Favourites, null),
	);
	const favouriteKeys = useMemo(
		() => new Set((favouriteBranch ?? []).map((item) => item.key)),
		[favouriteBranch],
	);

	const [moveModalItemId, setMoveModalItemId] = useState<string | null>(null);
	const [moveModalSourceParent, setMoveModalSourceParent] = useState<
		string | null
	>(null);
	const [manageBoardsOpen, setManageBoardsOpen] = useState(false);

	const { data: activeWhiteboards } = useQuery(
		fetchActiveWhiteboardsQueryOptions(
			authenticatedUser.id,
			shareKeySignal.value,
		),
	);
	const highestBoardIndexRef = useRef(0);

	const { mutateAsync: createCardInWorkspace, isPending: isCreatingCard } =
		useCreateCardInWorkspace();
	const { data: libraryCardsData } = useLibraryEntities({ type: "card" });
	const libraryCards = useMemo(
		() => libraryCardsData?.pages.flatMap((p) => p.nodes),
		[libraryCardsData],
	);
	const highestCardIndexRef = useRef(0);

	const navbarSection = NavbarSection.MyWhiteboards;

	const handleMoveToFolder = useCallback(
		(itemId: string) => {
			// Find which branch cache contains this item to get its current parent.
			// Branch query keys follow `navbarBranchQueryKeys.branch(section, parentKey)`
			// where parentKey is null for root-level items or a folder key for nested items.
			const branchKeys = findBranchKeysContainingInSection(
				queryClient,
				itemId,
				navbarSection,
			);
			const rawParentKey = branchKeys[0]?.[2] as string | undefined;
			const sourceParent =
				rawParentKey === "root" || rawParentKey == null ? null : rawParentKey;
			setMoveModalSourceParent(sourceParent);
			setMoveModalItemId(itemId);
		},
		[queryClient, navbarSection],
	);

	const handleDeleteFolderFromMenu = useCallback(
		(folderId: string) => {
			handleDeleteFolder(folderId, null);
		},
		[handleDeleteFolder],
	);

	const handleCreateBoardInFolder = useCallback(
		async (folderId: string) => {
			if (isCreatingWhiteboard) return;

			const whiteboardId = new WhiteboardDocumentId();
			const nextIndex = getNextNamedItemIndex(
				activeWhiteboards,
				highestBoardIndexRef,
			);
			const branchItems =
				queryClient.getQueryData<NavbarBranchItem[]>(
					navbarBranchQueryKeys.branch(navbarSection, folderId),
				) ?? [];
			const firstItemKey = branchItems[0]?.key;

			const { data } = await createWhiteboard({
				id: whiteboardId,
				name: generateWhiteboardName(nextIndex),
			});

			if (data?.id) {
				const whiteboardDocId = parseWhiteboardDocumentId(data.id);
				const boardKey = whiteboardDocId.toString();
				queryClient.setQueryData<NavbarBranchItem[]>(
					navbarBranchQueryKeys.branch(navbarSection, null),
					(old) =>
						old?.map((item) =>
							item.key === data.id ? { ...item, key: boardKey } : item,
						),
				);
				await moveRanklistItem({
					section: navbarSection,
					itemKey: boardKey,
					parentKey: folderId,
					sourceParentKey: null,
					targetIndex: 0,
					beforeKey: firstItemKey ?? null,
				});
				captureBoardCreationSucceeded(whiteboardDocId, "blank");
				setActiveSource("my-boards");
				navigateToWhiteboard(data.id);
			}
		},
		[
			activeWhiteboards,
			createWhiteboard,
			isCreatingWhiteboard,
			moveRanklistItem,
			navbarSection,
			navigateToWhiteboard,
			queryClient,
			setActiveSource,
		],
	);

	const handleCreateCardInFolder = useCallback(
		async (folderId: string) => {
			if (isCreatingCard) return;

			const newCardId = `Card-${nanoid()}`;
			const nextIndex = getNextNamedItemIndex(
				libraryCards,
				highestCardIndexRef,
			);
			const branchItems =
				queryClient.getQueryData<NavbarBranchItem[]>(
					navbarBranchQueryKeys.branch(navbarSection, folderId),
				) ?? [];
			const firstItemKey = branchItems[0]?.key;

			const createdCard = await createCardInWorkspace({
				section: navbarSection,
				name: `Card #${nextIndex}`,
				cardId: newCardId,
				parentFolderKey: folderId,
				beforeKey: firstItemKey ?? null,
			});

			if (createdCard?.key) {
				requestRename(createdCard.key);
				setActiveSource("my-boards");
				navigate(`/${newCardId}`);
			}
		},
		[
			createCardInWorkspace,
			isCreatingCard,
			libraryCards,
			navbarSection,
			navigate,
			queryClient,
			requestRename,
			setActiveSource,
		],
	);

	const handleCreateChatInFolder = useCallback(
		async (folderId: string) => {
			if (isCreatingChat) return;

			const created = await createChat({
				name: "New Chat",
				parentFolderKey: folderId,
			});
			if (created?.chatId) {
				const chatDocId = parseChatDocumentId(created.chatId).toString();
				setActiveSource("my-boards");
				navigate(`/${chatDocId}`);
				requestAnimationFrame(() => {
					document
						.querySelector<HTMLTextAreaElement>(
							'[data-ui-id="agent-chat-home"] textarea',
						)
						?.focus();
				});
			}
		},
		[createChat, isCreatingChat, navigate, setActiveSource],
	);

	const renderNavTreeItem = useMemo(
		() =>
			createNavTreeItemRenderer({
				favouriteKeys,
				createBoardDisabled: isCreatingWhiteboard,
				onCreateBoardInFolder: handleCreateBoardInFolder,
				onCreateCardInFolder: handleCreateCardInFolder,
				onCreateChatInFolder: handleCreateChatInFolder,
				onDeleteFolder: handleDeleteFolderFromMenu,
				onMoveToFolder: handleMoveToFolder,
				activeSource: "my-boards",
				navbarSection,
				showMoveToFolder: true,
				pendingRenameItemId,
				onPendingRenameHandled: consumeRename,
			}),
		[
			favouriteKeys,
			isCreatingWhiteboard,
			handleCreateBoardInFolder,
			handleCreateCardInFolder,
			handleCreateChatInFolder,
			handleDeleteFolderFromMenu,
			handleMoveToFolder,
			navbarSection,
			pendingRenameItemId,
			consumeRename,
		],
	);

	if (isLoading) {
		return <NavSectionLoadingItem />;
	}

	return (
		<div id="nav-tree-panel" className="rounded-lg w-0 min-w-full">
			{hideSectionHeader ? null : (
				<div
					className="inline-flex items-center justify-center max-w-full min-w-full cursor-pointer whitespace-nowrap gap-0.5 h-8 bg-surface-default sticky top-0 z-10"
					onClick={() => setIsCollapsed((v) => !v)}
				>
					<ThemeIcon
						variant="transparent"
						size="xs"
						color="var(--ui-color-text-primary)"
					>
						{!isCollapsed ? <IconCaretDownFilled /> : <IconCaretRightFilled />}
					</ThemeIcon>

					<NavSectionTextLabel
						className="grow pointer-events-auto select-none max-w-full ml-2 font-bold text-sm sm:text-xs text-text-secondary uppercase"
						label="My Workspace"
						size="sm"
					/>

					<Tooltip
						label="Manage workspace"
						openDelay={200}
						zIndex={9999}
						withArrow
					>
						<ActionIcon
							variant="subtle"
							size="md"
							color="var(--ui-color-text-muted)"
							aria-label="Manage workspace"
							title="Manage workspace"
							onClick={(e) => {
								e.stopPropagation();
								setManageBoardsOpen(true);
							}}
						>
							<IconAdjustmentsHorizontal width={16} height={16} />
						</ActionIcon>
					</Tooltip>
					<Tooltip
						label="Create card in Workspace"
						openDelay={200}
						zIndex={9999}
						withArrow
					>
						<ActionIcon
							variant="subtle"
							size="md"
							color="var(--ui-color-text-muted)"
							aria-label="Create card in Workspace"
							disabled={isCreatingCard}
							onClick={async (e) => {
								e.stopPropagation();
								if (isCreatingCard) return;

								const newCardId = `Card-${nanoid()}`;
								const nextIndex = getNextNamedItemIndex(
									libraryCards,
									highestCardIndexRef,
								);

								const createdCard = await createCardInWorkspace({
									section: navbarSection,
									name: `Card #${nextIndex}`,
									cardId: newCardId,
								});

								if (createdCard?.key) {
									requestRename(createdCard.key);
									setActiveSource("my-boards");
									navigate(`/${newCardId}`);
								}
							}}
						>
							{isCreatingCard ? (
								<Loader size={14} color="currentColor" />
							) : (
								<IconCardPlus width={16} height={16} />
							)}
						</ActionIcon>
					</Tooltip>
					<Tooltip label="New Folder" openDelay={200} zIndex={9999} withArrow>
						<ActionIcon
							variant="subtle"
							size="md"
							color="var(--ui-color-text-muted)"
							aria-label="New folder"
							disabled={isCreatingFolder}
							onClick={async (e) => {
								e.stopPropagation();
								const createdFolderId = await handleCreateFolder(null, {
									id: nanoid(),
									isFolder: true,
									name: "New Folder",
								});

								if (createdFolderId) {
									requestRename(createdFolderId);
								}
							}}
						>
							<IconFolderPlus width={16} height={16} />
						</ActionIcon>
					</Tooltip>
				</div>
			)}

			{(hideSectionHeader || !isCollapsed) && rootItems.length > 0 && (
				<div data-nav-tree-container="true">
					<Tree
						ref={treeRef}
						useBranchItems={useNavBranchItems}
						renderItem={renderNavTreeItem}
						indentPerLevel={TREE_INDENT_PER_LEVEL_PX}
						dragAndDropEnabled={treeDragAndDropEnabled}
						initialOpenState={initialOpenState}
						onOpenStateChange={(id, isOpen) => {
							const state = readTreeOpenState(OPEN_STATE_KEY);
							state[id] = isOpen;
							localStorage.setItem(OPEN_STATE_KEY, JSON.stringify(state));
						}}
						onMoveItem={handleMoveItem}
						onCreateFolder={handleCreateFolder}
						onDeleteFolder={handleDeleteFolder}
						renderLoading={() => <NavSectionLoadingItem />}
					/>
				</div>
			)}

			{hideSectionHeader && rootItems.length === 0 && (
				<div className="flex flex-col items-center justify-center py-10 text-center text-text-muted px-4">
					<p className="text-xs font-medium">No boards or folders yet</p>
					<p className="text-[11px] text-text-secondary mt-1">
						Create a new board using the + button above to get started.
					</p>
				</div>
			)}

			<MoveToFolderModal
				opened={moveModalItemId !== null}
				onClose={() => setMoveModalItemId(null)}
				itemKey={moveModalItemId ?? ""}
				sourceParentKey={moveModalSourceParent}
				section={navbarSection}
			/>

			<ManageBoardsModal
				opened={manageBoardsOpen}
				onClose={() => setManageBoardsOpen(false)}
				section={navbarSection}
			/>
		</div>
	);
}
